<template>
  <div class="widget w-heatmap-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header hideLinkModeReceive printView :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <v-form-group :label="i18n('controls.symbol')" label-cols="auto">
        <v-form-select :options="symbolOptions" v-model="controlSymbol" :aria-label="i18n('controls.symbol')" />
      </v-form-group>

      <v-form-group :label="i18n('controls.indicator')" label-cols="auto">
        <v-form-select :options="controlsOptions" v-model="controlIndicator" :aria-label="i18n('controls.indicator')" />
      </v-form-group>

      <v-form-group :label="i18n('controls.size')" label-cols="auto">
        <v-form-select :options="[controlsOptions[1]]" v-model="controlSize" :aria-label="i18n('controls.size')">
          <template v-slot:first>
            <v-form-select-option value="equal">{{ i18n('labels.allEqual') }}</v-form-select-option>
          </template>
        </v-form-select>
      </v-form-group>

      <v-form-group :label="i18n('controls.sort')" label-cols="auto">
        <v-form-select :options="controlsOptions" v-model="controlSort" :aria-label="i18n('controls.sort')">
          <template v-slot:first>
            <v-form-select-option value="size">{{ i18n('labels.size') }}</v-form-select-option>
            <v-form-select-option value="x._TICKER">{{ i18n('labels.alphabetically') }}</v-form-select-option>
          </template>
        </v-form-select>
      </v-form-group>

      <v-form-group>
        <v-form-select v-model="controlSortDir" :aria-label="i18n('controls.sort')">
          <v-form-select-option value="asc">{{ i18n('labels.ASC') }}</v-form-select-option>
          <v-form-select-option value="desc">{{ i18n('labels.DESC') }}</v-form-select-option>
        </v-form-select>
      </v-form-group>

      <v-form-group :label="i18n('controls.group')" label-cols="auto">
        <v-form-select v-model="controlGroup" :aria-label="i18n('controls.group')">
          <v-form-select-option value="">{{ i18n('labels.none') }}</v-form-select-option>
          <v-form-select-option value="sector">{{ i18n('labels.sector') }}</v-form-select-option>
        </v-form-select>
      </v-form-group>

      <v-form-group :label="i18n('autosort', 'globalLabels')" label-cols="auto">
        <c-widget-input-switch :options="autosortOptions" v-model="controlAutosort" />
      </v-form-group>

      <div class="sr-only">
        {{ i18n('labels.information') }}
      </div>
    </c-widget-control-bar>

    <c-streaming-error :show="hasStreamingError" />

    <div class="widget-body" v-resize:debounce.30="onResize">
      <c-loader v-if="isLoading" />
      <v-alert show v-if="hasError" variant="red" align="center">{{ i18n('error', 'globalMessages') }}</v-alert>
      <div v-else class="map-container" ref="mapContainer"></div>
      <b-toaster :name="notifierId"></b-toaster>
      <div class="tooltip" aria-live="assertive" aria-atomic="true"></div>
    </div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import resize from 'vue-resize-directive';
import widgetCommons from '@mixins/widgetCommons';
import * as d3 from 'd3';
import { API } from '@/state/api';
import { contrast, rgb } from '@/utils/colors';
import { getConsolidatedRic } from '@/utils/helpers';

export default {
  name: 'w-heatmap-widget',
  mixins: [widgetCommons],
  components: { CLoader, CWidgetControlBar, CWidgetHeader, CWidgetInputSwitch, CStreamingError },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  directives: {
    resize
  },
  data() {
    return {
      firstRender: true,
      hasError: false,
      hasStreamingError: false,
      isLoading: true,
      lastRenderTime: 0,
      rawData: null,
      tooltipEl: null,
      transitionLength: 700,
      treemap: null,
      treemapModel: null,
      widgetViewportSize: []
    };
  },
  computed: {
    autosortOptions() {
      return [
        {
          value: true,
          text: this.i18n('on', 'globalLabels')
        },
        {
          value: false,
          text: this.i18n('off', 'globalLabels')
        }
      ];
    },
    controlAutosort: {
      get() {
        return this.widgetStore.autosort;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/AUTOSORT_CHANGED`, val);
      }
    },
    controlGroup: {
      get() {
        return this.widgetStore.group;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/GROUP_CHANGED`, val);
        this.renderData();
      }
    },
    controlIndicator: {
      get() {
        return this.widgetStore.indicator;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/INDICATOR_CHANGED`, val);
        this.renderData();
      }
    },
    controlSize: {
      get() {
        return this.widgetStore.size;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SIZE_CHANGED`, val);
        this.renderData();
      }
    },
    controlSort: {
      get() {
        return this.widgetStore.sort;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SORT_CHANGED`, val);
        this.renderData();
      }
    },
    controlSortDir: {
      get() {
        return this.widgetStore.sortDir;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SORT_DIR_CHANGED`, val);
        this.renderData();
      }
    },
    controlSymbol: {
      get() {
        return this.widgetStore.symbol;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SYMBOL_CHANGED`, val);
        this.fetch();
      }
    },
    controlsOptions() {
      return [
        {
          key: 'performance',
          label: this.i18n('labels.performance'),
          options: [
            { value: 'q._PCTCHNG', text: this.i18n('labels.performance') },
            { value: 'd._PCTCHG_1W', text: `${this.i18n('labels.performance')} ${this.i18n('labels.oneWeek')}` },
            { value: 'd._PCTCHG_1M', text: `${this.i18n('labels.performance')} ${this.i18n('labels.oneMonth')}` },
            { value: 'd._PCTCHG_1Y', text: `${this.i18n('labels.performance')} ${this.i18n('labels.oneYear')}` }
          ]
        },
        {
          key: 'tradeData',
          label: this.i18n('labels.tradeData'),
          options: [
            { value: 'd._VOLA_1W', text: `${this.i18n('labels.vola')} ${this.i18n('labels.oneWeek')}` },
            { value: 'd._VOLA_1M', text: `${this.i18n('labels.vola')} ${this.i18n('labels.oneMonth')}` },
            { value: 'd._VOLA_1Y', text: `${this.i18n('labels.vola')} ${this.i18n('labels.oneYear')}` },
            { value: 'q._ACVOL_1', text: this.i18n('labels.tradedVolume') },
            { value: 'd._ACVOL_1W', text: `${this.i18n('labels.tradedVolume')} ${this.i18n('labels.oneWeek')}` },
            { value: 'd._ACVOL_1M', text: `${this.i18n('labels.tradedVolume')} ${this.i18n('labels.oneMonth')}` },
            { value: 'd._ACVOL_1Y', text: `${this.i18n('labels.tradedVolume')} ${this.i18n('labels.oneYear')}` },
            { value: 'q._TURNOVER', text: this.i18n('labels.turnover') },
            { value: 'rkd.TOTALFLOAT_PCT', text: this.i18n('labels.freeFloat') },
            { value: 'rkd.MKTCAP_MOREY', text: this.i18n('labels.marketCap') }
          ]
        },
        {
          key: 'fundamentals',
          label: this.i18n('labels.fundamentals'),
          options: [
            { value: 'rkd.EPS_CHANGE_CURRY_TO_LST1Y', text: this.i18n('labels.earningsChangePrevYear') },
            { value: 'rkd.EPS_TO_PRICE', text: this.i18n('labels.earningsYield') },
            { value: 'rkd.DPS_TO_PRICE', text: this.i18n('labels.dividendYield') },
            { value: 'rkd.PEG', text: this.i18n('labels.PEG') },
            { value: 'rkd.PRICE_TO_EPS_NXT1Y', text: this.i18n('labels.forwardPE') },
            { value: 'rkd.PRICE_TO_EPS', text: this.i18n('labels.PE') },
            { value: 'rkd.PRICE_TO_BVPS', text: this.i18n('labels.PB') },
            { value: 'rkd.PRICE_TO_CPS', text: this.i18n('labels.PC') },
            { value: 'rkd.PRICE_TO_NET_SALES', text: this.i18n('labels.PS') }
          ]
        }
      ];
    },
    symbolOptions() {
      return [
        // for testing purposes
        // {
        //   label: 'European Indices',
        //   options: [{ text: 'DAX', value: '.GDAXI' }]
        // },
        {
          label: this.i18n('indicesSectors.canadianIndices', 'globalLabels'),
          options: [
            { text: 'TSX', value: '.GSPTSE' },
            { text: 'TSX 60', value: '.SPTSE' },
            { text: 'TSX Venture', value: '.SPCDNX' },
            { text: 'TSX Small Cap', value: '.SPTSES' }
          ]
        },
        {
          label: this.i18n('indicesSectors.canadianSectors', 'globalLabels'),
          options: [
            { text: 'CAN CONS DISCR', value: '.GSPTTCD' },
            { text: 'CAN CONS STPLS', value: '.GSPTTCS' },
            { text: 'CAN GOLD NDX', value: '.SPTTGD' },
            { text: 'CAN HLTH CARE', value: '.GSPTTHC' },
            { text: 'CAN INDUSTRIALS', value: '.GSPTTIN' },
            { text: 'CAN REAL ESTATE', value: '.GSPTTRE' },
            { text: 'CAN TECH INDEX', value: '.SPTTTK' },
            { text: 'S&P/TSX Capped Communication Service', value: '.GSPTTTS' },
            { text: 'S&P/TSX Capped Financials Index', value: '.SPTTFS' },
            { text: 'S&P/TSX Capped Materials Index', value: '.GSPTTMT' },
            { text: 'S&P/TSX ENERGY', value: '.SPTTEN' },
            { text: 'S&P/TSX UTILS', value: '.GSPTTUT' }
          ]
        },
        {
          label: this.i18n('indicesSectors.usIndices', 'globalLabels'),
          options: [
            { text: 'Dow Jones', value: '.DJI' },
            { text: 'S&P 500', value: '.SPX' },
            { text: 'Nasdaq 100', value: '.NDX' },
            // { text: 'Russell 2000', value: '.RUT' },
            { text: 'S&P 100', value: '.OEX' }
            // { text: 'NYSE Composite', value: '.NYA' }
          ]
        },
        {
          label: this.i18n('indicesSectors.usSectors', 'globalLabels'),
          options: [
            { text: 'S&P 500 Energy', value: '.5SP10' },
            { text: 'S&P 500 Materials', value: '.5SP15' },
            { text: 'S&P 500 Industrials', value: '.5SP20' },
            { text: 'S&P 500 Cons. Discr.', value: '.5SP25' },
            { text: 'S&P 500 Cons. Staples', value: '.5SP30' },
            { text: 'S&P 500 Health Care', value: '.5SP35' },
            { text: 'S&P 500 Financials', value: '.5SP40' },
            { text: 'S&P 500 Technology', value: '.5SP45' },
            { text: 'S&P 500 Communication', value: '.5SP50' },
            { text: 'S&P 500 Utilities', value: '.5SP55' }
          ]
        },
        {
          label: this.i18n('labels.watchlists'),
          options: [
            ...this.watchlists.map(watchlist => ({
              text: watchlist.name,
              value: watchlist.id
            }))
          ]
        }
      ];
    },
    theme() {
      return this.$store.state.applicationSettings.general.settings.theme;
    },
    colorRange() {
      // we could use document.body here, but then this prop wouldn't react to theme changes
      const container = document.querySelector(`.theme--${this.theme}`);
      const styles = getComputedStyle(container);
      if (this.controlIndicator.includes('VOLA')) {
        return [styles.getPropertyValue('--pink-lighten-2').trim(), styles.getPropertyValue('--pink-darken-2').trim()];
      }
      if (
        this.controlsOptions
          .find(optgroup => optgroup.key === 'fundamentals')
          .options.find(option => option.value === this.controlIndicator)
      ) {
        return [
          styles.getPropertyValue('--orange-lighten-2').trim(),
          styles.getPropertyValue('--orange-darken-2').trim()
        ];
      }
      if (this.controlIndicator.includes('PCTCH')) {
        const midtone = styles.getPropertyValue(this.theme === 'light' ? '--gray-200' : '--gray-700').trim();
        return [
          styles.getPropertyValue('--negative-lighten-1').trim(),
          midtone,
          styles.getPropertyValue('--positive-lighten-1').trim()
        ];
      }
      return [styles.getPropertyValue('--blue-darken-2').trim(), styles.getPropertyValue('--blue-lighten-2').trim()];
    },
    groupElHeight() {
      return {
        xs: 15,
        s: 18,
        m: 21,
        l: 24,
        xl: 27
      }[this.fontSize];
    },
    lang() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage.substr(0, 2);
    },
    requestFids() {
      return [
        'q.RIC',
        'q._CURRENCY',
        'q._TYPE',
        'x._TICKER',
        'name._SHORTNAME',
        'rkd.COMP_TAXONOMY_TRBC_CD_L1',
        ...this.controlsOptions.reduce((acc, optgroup) => {
          acc.push(...optgroup.options.map(option => option.value));
          return acc;
        }, [])
      ];
    },
    streamingFids() {
      return this._.intersection(
        ['q._PCTCHNG', 'q._TURNOVER', 'q._ACVOL_1'],
        [this.controlIndicator, this.controlSize, this.controlSort]
      );
    },
    tooltipFids() {
      return [this.controlIndicator, this.controlSize, this.controlSort].reduce((acc, fid) => {
        if (/^(?!name)\w+\./.test(fid) && !acc.includes(fid)) acc.push(fid);
        return acc;
      }, []);
    },
    watchlists() {
      return this.$store.getters['watchlists/watchlistsSorted'];
    },
    accessibilityFeatures() {
      return this.$store.state.applicationSettings.accessibility.settings.accessibilityFeatures;
    }
  },
  watch: {
    lang() {
      this.renderData();
    },
    streamingFids(newVal, oldVal) {
      if (newVal.toString() !== oldVal.toString()) {
        this.streamingUnsubscribe();
        this.streamingSubscribe();
      }
    },
    theme() {
      this.renderData();
    }
  },
  methods: {
    onResize() {
      if (!this.rawData) return;
      this.setHeatmapSize();
      this.renderData();
    },
    setHeatmapSize() {
      const containerEl = this.$el.querySelector('.widget-body');
      this.heatmapViewportSize = {
        width: containerEl.offsetWidth,
        height: containerEl.offsetHeight
      };
      this.treemap
        .size([this.heatmapViewportSize.width - 1, this.heatmapViewportSize.height])
        .paddingTop(d => (d.depth === 1 ? this.groupElHeight : 0));
    },
    updateLink(e) {
      this.sendLinkUpdate(e);
    },
    fetch() {
      this.streamingUnsubscribe();
      this.isLoading = true;
      this.hasError = false;
      let uri;
      let watchlist = this.watchlists.find(w => w.id === this.controlSymbol);
      if (watchlist) {
        uri = `/quote/info?rics=${watchlist.items}&fids=` + this.requestFids;
      } else {
        uri = `/index/constituents?ric=${getConsolidatedRic(this.controlSymbol)}&fids=` + this.requestFids;
      }

      API.Rest.get(uri).then(response => {
        if (!response.data.data.length) {
          console.error('No data for ' + this.controlSymbol);
          this.hasError = true;
          this.isLoading = false;
          return;
        }
        this.hasError = false;
        this.snapshotData = {};
        this.rawData = response.data.data.reduce((acc, dataRow) => {
          this.snapshotData[dataRow['q.RIC']] = { ...dataRow };
          acc[dataRow['q.RIC']] = { ...dataRow };
          return acc;
        }, {});
        this.formats = Object.keys(response.data.metadata).reduce((acc, fid) => {
          acc[fid] = response.data.metadata[fid].type;
          return acc;
        }, {});
        this.streamingSubscribe();
      });
    },
    streamingUnsubscribe() {
      if (!this.subscriptionID) return;
      API.Streaming.Quote.unsubscribe(this.subscriptionID);
    },
    streamingSubscribe() {
      if (!this.streamingFids.length) {
        this.renderData();
        this.isLoading = false;
        return;
      }
      API.Streaming.connect({
        timeout: 10000,
        monitor: {
          interval: 10000,
          timeout: 5000
        },
        visibilityMonitorEnabled: true
      }).then(() => {
        this.subscriptionID = API.Streaming.Quote.subscribe(
          {
            rics: Object.keys(this.rawData),
            fids: this.streamingFids
          },
          // Subscribe Callback
          (_res, error) => {
            if (error) {
              console.error('Streaming subscription error', error);
              this.hasStreamingError = true;
            } else {
              this.hasStreamingError = false;
            }
          },
          // snapshot
          this.handleSnapshot,
          // update
          this.handleUpdate
        );
      });
    },
    handleSnapshot(response, error) {
      if (error) {
        console.warn('Streaming snapshot error', error);
        if (this.isLoading) {
          this.hasError = true;
          this.isLoading = false;
        } else {
          this.hasStreamingError = true;
        }
        return;
      }
      this.hasStreamingError = false;
      this.handleUpdate(response);
      this.isLoading = false;
    },
    handleUpdate(response, error) {
      if (error) {
        console.warn('Streaming update error', error);
        this.hasStreamingError = true;
        return;
      }
      this.hasStreamingError = false;
      if (response) {
        response = response.payload[0];
        Object.assign(this.rawData[response.ric], response.data);
        if (this.controlAutosort) Object.assign(this.snapshotData[response.ric], response.data);
        // cushion heatmap rerender times a bit, it's struggling with animation otherwise
        if (Date.now() - this.lastRenderTime >= this.transitionLength + 50) {
          this.renderData(this.controlAutosort);
        }
      }
    },
    formatFid(fid, dataRow) {
      let value = dataRow[fid];
      let output = value;
      // the following formats big numbers in readable manner. Eg.: 1000000 -> 1 mln
      // it also adjusts the decimal number length depending on the total digits count: max 5 digits for numbers over 1 mln, max 6 digits otherwise
      if (!isNaN(value)) {
        const klvlMap = { 2: 'millions', 3: 'billions', 4: 'trillions' };
        if (fid === 'rkd.MKTCAP_MOREY') value *= 1000000;
        const klvl = Math.floor((String(+value).match(/\d+/)[0].length - 1) / 3);
        const maxNumberLength = klvl > 1 ? 5 : 6;
        if (klvl > 1) output = value / Math.pow(1000, klvl);
        const factor = Math.pow(10, Math.min(3, maxNumberLength - String(output).match(/\d+/)[0].length));
        output = this.$f(Math.round(output * factor) / factor, this.formats[fid]);
        if (klvl > 1) output += ' ' + this.i18n('short.' + klvlMap[klvl], 'globalLabels');
        if (['q._TURNOVER', 'rkd.MKTCAP_MOREY'].includes(fid)) {
          output = this.formatCurrency(dataRow['q._CURRENCY'], output);
        }
      } else output = this.$f(value, this.formats[fid]);
      return output;
    },
    renderData(autosort = true) {
      // min/max indicator values for color tones spreading
      const extremes = [0, 0];
      const treeData = { name: 'heatmap', children: [] };
      Object.values(this.rawData).forEach(dataRow => {
        let children = treeData.children;
        // create data hierarchy, when grouping is active
        if (this.controlGroup) {
          let groupName;
          switch (this.controlGroup) {
            case 'sector':
              groupName = this.i18n(dataRow['rkd.COMP_TAXONOMY_TRBC_CD_L1'], 'sectors');
              break;
          }
          let groupObj = children.find(o => o.name === groupName);
          if (!groupObj) {
            groupObj = {
              name: groupName,
              children: []
            };
            children.push(groupObj);
          }
          children = groupObj.children;
        }
        children.push({
          indicator: +dataRow[this.controlIndicator],
          indicatorFormatted: this.formatFid(this.controlIndicator, dataRow),
          name: dataRow['x._TICKER'],
          ric: dataRow['q.RIC'],
          size: this.controlSize === 'equal' ? 1 : isNaN(dataRow[this.controlSize]) ? 0 : +dataRow[this.controlSize],
          verboseName: dataRow['name._SHORTNAME'],
          groupName: this.i18n(dataRow['rkd.COMP_TAXONOMY_TRBC_CD_L1'], 'sectors')
        });
        if (!isNaN(dataRow[this.controlIndicator])) {
          if (+dataRow[this.controlIndicator] < extremes[0]) extremes[0] = +dataRow[this.controlIndicator];
          else extremes[1] = Math.max(extremes[1], +dataRow[this.controlIndicator]);
        }
      });
      // build virtual tiles tree
      const treemapModel = this.treemap(
        d3
          .hierarchy(treeData)
          .sum(entity => entity.size)
          .sort((a, b) => {
            // always sort groups by size
            if (a.height) return d3[this.controlSortDir + 'ending'](a.value, b.value);
            const sortField = this.controlSort === 'size' ? this.controlSize : this.controlSort;
            const data = autosort ? this.rawData : this.snapshotData;
            return d3[this.controlSortDir + 'ending'](
              isNaN(data[a.data.ric][sortField]) ? 0 : +data[a.data.ric][sortField],
              isNaN(data[b.data.ric][sortField]) ? 0 : +data[b.data.ric][sortField]
            );
          })
      );
      let nodesContainer = d3.select(this.$refs.mapContainer);
      // handle group nodes
      nodesContainer
        .selectAll('.group')
        .data(this.controlGroup ? treemapModel.children : [], d => d.data.name)
        .join('div')
        .attr('class', 'group')
        .attr('aria-hidden', 'true')
        .style('transform', d => `translate(${d.x0 + 1}px, ${d.y0 + 1}px)`)
        .style('width', d => d.x1 - d.x0 - 2 + 'px')
        .style('height', d => d.children[0].y0 - d.y0 - 2 + 'px')
        .attr('title', d => d.data.name)
        .text(d => d.data.name);
      // handle symbol nodes
      const nodes = nodesContainer
        .selectAll('.node')
        .data(treemapModel.leaves(), d => d.data.ric)
        .join(
          enter => {
            const tileEls = enter
              .append('div')
              .classed('node', true)
              .attr('tabindex', 0)
              .attr('role', 'button')
              .style('opacity', 0)
              .on('mouseleave mousedown keyup', this.handleTileMouseEvents)
              .on('click', h => this.updateLink(h.data.ric));
            const container = tileEls
              .append('div')
              .style('transform', 'translateY(-50%)')
              .style('position', 'absolute')
              .style('top', '50%')
              .classed('label', true);
            container
              .append('div')
              .classed('name', true)
              .attr('aria-hidden', true);
            container
              .append('div')
              .classed('value', true)
              .attr('aria-hidden', true);

            return tileEls;
          },
          update => update,
          exit => {
            exit
              .transition()
              .duration(this.transitionLength)
              .style('width', '0px')
              .style('height', '0px')
              .style('left', '0px')
              .style('top', '0px')
              .style('opacity', '0')
              .remove();
          }
        );

      if (nodes) {
        const colorRange = d3
          .scaleLinear()
          .domain(this.colorRange.length === 3 ? [extremes[0], 0, extremes[1]] : extremes)
          .range(this.colorRange)
          .clamp(true);
        nodes
          .transition()
          .duration(this.firstRender ? 0 : this.transitionLength)
          .style('left', d => d.x0 + 'px')
          .style('top', d => d.y0 + 'px')
          .style('width', d => d.x1 - d.x0 + 'px')
          .style('height', d => d.y1 - d.y0 + 'px')
          .style('opacity', '')
          .style('background-color', node => colorRange(isNaN(node.data.indicator) ? 0 : node.data.indicator))
          .style('color', node => {
            const bg = rgb.toArray(colorRange(isNaN(node.data.indicator) ? 0 : node.data.indicator));
            return contrast(bg, [255, 255, 255]) >= 4.5 ? 'white' : 'black';
          })
          .each((node, i, els) => {
            const width = node.x1 - node.x0;
            const height = node.y1 - node.y0;
            const nameEl = els[i].querySelector('.name');
            nameEl.innerText = node.data.name;
            const valueEl = els[i].querySelector('.value');
            valueEl.innerText = node.data.indicatorFormatted;

            if (this.accessibilityFeatures) {
              const sectorName =
                node.parent.data.name !== 'heatmap' ? `${node.parent.data.name} ${this.i18n('labels.sector')}` : '';
              els[i].ariaLabel = `${node.data.name} ${node.data.indicatorFormatted} ${sectorName} `;
            }

            const labelEl = els[i].querySelector('.label');
            labelEl.style.width = width + 'px';
            labelEl.style.visibility =
              labelEl.scrollWidth > Math.round(width) || labelEl.scrollHeight > Math.round(height) ? 'hidden' : '';
          });

        this.firstRender = false;
        this.lastRenderTime = Date.now();
      }
    },
    handleTileMouseEvents(node) {
      const event = d3.event;

      if (event.type === 'mousedown' || event.keyCode === 13 || event.keyCode === 32) {
        if (this.tooltipEl.style.opacity !== '1') {
          this.tooltipEl.innerHTML = `
              <div>${node.data.verboseName} <span>(${node.data.name})</span></div>
                ${this.tooltipFids
                  .map(fid => {
                    for (const optgroup of this.controlsOptions) {
                      for (let option of optgroup.options) {
                        if (option.value === fid)
                          return `<div>${option.text}: ${this.formatFid(fid, this.rawData[node.data.ric])}</div>`;
                      }
                    }
                  })
                  .join('\n')}
             `;
          this.tooltipEl.style.opacity = 1;
          this.tooltipEl.style.borderColor = node.color;
          this.tooltipEl._dimensions = { width: this.tooltipEl.offsetWidth, height: this.tooltipEl.offsetHeight };
          let posX = node.x1 + 6;
          let isLeftSide = false;
          if (posX + this.tooltipEl._dimensions.width > this.heatmapViewportSize.width) {
            posX = node.x0 - this.tooltipEl._dimensions.width - 6;
            isLeftSide = true;
          }
          let posY = Math.min(
            this.heatmapViewportSize.height - this.tooltipEl._dimensions.height,
            Math.max(0, node.y0 + d3.event.offsetY - this.tooltipEl._dimensions.height / 2)
          );
          this.tooltipEl.style.left = posX + 'px';
          this.tooltipEl.style.top = posY + 'px';
          this.tooltipEl.classList[isLeftSide ? 'add' : 'remove']('left');
        } else {
          this.tooltipEl.style.opacity = 0;
        }
      } else if (event.type === 'mouseleave' || event.keyCode === 9) {
        this.tooltipEl.style.opacity = 0;
      }
    }
  },
  mounted() {
    this.treemap = d3
      .treemap()
      .paddingInner(1)
      .paddingOuter(1)
      .round(true);
    this.setHeatmapSize();
    this.tooltipEl = this.$el.querySelector('.tooltip');
    this.fetch();
  },
  destroyed() {
    this.streamingUnsubscribe();
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/heatmap-widget';
</style>
